<template>
  <div class="payments-page">
    <!-- Background elements -->
    <div class="bg-grid"></div>
    <div class="bg-glow"></div>

    <div class="payments-container">
      <div class="page-header">
        <h1>Payments</h1>
        <p class="subtitle">View all payment transactions</p>
      </div>

      <!-- Loading State -->
      <div v-if="loading" class="loading-state">
        <div class="spinner"></div>
        <p>Loading payments...</p>
      </div>

      <!-- Error State -->
      <div v-else-if="error" class="error-state">
        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
          <circle cx="12" cy="12" r="10"></circle>
          <line x1="12" y1="8" x2="12" y2="12"></line>
          <line x1="12" y1="16" x2="12.01" y2="16"></line>
        </svg>
        <p>{{ error }}</p>
        <button class="retry-btn" @click="fetchPayments">Try Again</button>
      </div>

      <!-- Empty State -->
      <div v-else-if="!payments || payments.length === 0" class="empty-state">
        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
          <rect x="1" y="4" width="22" height="16" rx="2" ry="2"></rect>
          <line x1="1" y1="10" x2="23" y2="10"></line>
        </svg>
        <p>No payment transactions found.</p>
      </div>

      <!-- Transactions Table -->
      <div v-else class="transactions-table-wrapper">
        <table class="transactions-table">
          <thead>
            <tr>
              <th>Date</th>
              <th>User Name</th>
              <th>Subscription</th>
              <th>Amount</th>
              <th>Gateway</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="payment in payments" :key="payment.paymentGatewayTransactionID">
              <td class="date-cell">
                {{ formatDate(payment.paymentDate) }}
              </td>
              <td class="user-name-cell">
                {{ payment.userName || 'N/A' }}
              </td>
              <td class="subscription-cell">
                {{ payment.subscriptionName || 'N/A' }}
              </td>
              <td class="amount-cell">
                {{ formatCurrency(payment.paymentAmount, payment.currency) }}
              </td>
              <td class="gateway-cell">
                <span class="gateway-badge" :class="getGatewayClass(payment.paymentGateway)">
                  {{ formatGateway(payment.paymentGateway) }}
                </span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted } from 'vue'
import { api } from '../../utils/api'
import { useRouter } from 'vue-router'

export default {
  name: 'AdminPaymentsView',
  setup() {
    const router = useRouter()
    const payments = ref([])
    const loading = ref(true)
    const error = ref('')

    const fetchPayments = async () => {
      try {
        loading.value = true
        error.value = ''
        const data = await api('/admin/payments', {}, router)
        payments.value = data?.payments || []
      } catch (err) {
        console.error('Failed to fetch payments:', err)
        error.value = err.code || 'Something went wrong please contact support'
      } finally {
        loading.value = false
      }
    }

    const formatDate = (dateString) => {
      if (!dateString) return 'N/A'
      const date = new Date(dateString)
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    }

    const formatCurrency = (amount, currency) => {
      if (amount === null || amount === undefined) return 'N/A'
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currency || 'USD'
      }).format(amount)
    }

    const formatGateway = (gateway) => {
      if (!gateway) return 'N/A'
      return gateway.charAt(0).toUpperCase() + gateway.slice(1).toLowerCase()
    }

    const getGatewayClass = (gateway) => {
      if (!gateway) return ''
      const normalized = gateway.toLowerCase()
      if (normalized === 'paddle') {
        return 'paddle'
      } else if (normalized === 'stripe') {
        return 'stripe'
      }
      return ''
    }

    onMounted(() => {
      fetchPayments()
    })

    return {
      payments,
      loading,
      error,
      fetchPayments,
      formatDate,
      formatCurrency,
      formatGateway,
      getGatewayClass
    }
  }
}
</script>

<style scoped>
.payments-page {
  position: relative;
  min-height: 100vh;
  padding: 40px 20px;
  overflow: hidden;
}

/* Background elements */
.bg-grid {
  position: absolute;
  inset: 0;
  background-image: 
    linear-gradient(rgba(59, 130, 246, 0.03) 1px, transparent 1px),
    linear-gradient(90deg, rgba(59, 130, 246, 0.03) 1px, transparent 1px);
  background-size: 60px 60px;
  pointer-events: none;
}

.bg-glow {
  position: absolute;
  top: 20%;
  left: 50%;
  transform: translateX(-50%);
  width: 600px;
  height: 400px;
  background: radial-gradient(ellipse, rgba(59, 130, 246, 0.15) 0%, transparent 70%);
  pointer-events: none;
  animation: pulse-glow 8s ease-in-out infinite;
}

@keyframes pulse-glow {
  0%, 100% { opacity: 0.6; transform: translateX(-50%) scale(1); }
  50% { opacity: 1; transform: translateX(-50%) scale(1.1); }
}

.payments-container {
  position: relative;
  z-index: 1;
  max-width: 1400px;
  margin: 0 auto;
}

.page-header {
  margin-bottom: 32px;
  animation: fade-in 0.6s ease-out;
}

.page-header h1 {
  color: var(--text-primary);
  font-size: 36px;
  font-weight: 700;
  margin: 0 0 8px 0;
}

.subtitle {
  color: var(--text-secondary);
  font-size: 16px;
  margin: 0;
}

/* Loading, Error, Empty states */
.loading-state,
.error-state,
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 20px;
  gap: 20px;
  animation: fade-in 0.4s ease-out;
}

.loading-state p,
.error-state p,
.empty-state p {
  color: var(--text-secondary);
  font-size: 16px;
  margin: 0;
}

.error-state svg,
.empty-state svg {
  color: var(--text-muted);
}

.error-state svg {
  color: #ef4444;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 3px solid rgba(59, 130, 246, 0.2);
  border-top-color: var(--accent-color);
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.retry-btn {
  padding: 10px 24px;
  background: rgba(59, 130, 246, 0.15);
  color: var(--accent-color);
  border: 1px solid rgba(59, 130, 246, 0.3);
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
}

.retry-btn:hover {
  background: rgba(59, 130, 246, 0.25);
  border-color: rgba(59, 130, 246, 0.5);
}

@keyframes fade-in {
  from { opacity: 0; transform: translateY(-20px); }
  to { opacity: 1; transform: translateY(0); }
}

/* Transactions Table */
.transactions-table-wrapper {
  background: rgba(30, 41, 59, 0.6);
  backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  overflow: hidden;
  animation: slide-up 0.6s ease-out;
}

.transactions-table {
  width: 100%;
  border-collapse: collapse;
}

.transactions-table thead {
  background: rgba(0, 0, 0, 0.2);
}

.transactions-table th {
  padding: 16px;
  text-align: left;
  color: var(--text-primary);
  font-size: 14px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.transactions-table tbody tr {
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  transition: background-color 0.2s ease;
}

.transactions-table tbody tr:hover {
  background: rgba(255, 255, 255, 0.05);
}

.transactions-table tbody tr:last-child {
  border-bottom: none;
}

.transactions-table td {
  padding: 16px;
  color: var(--text-secondary);
  font-size: 14px;
}

.date-cell {
  color: var(--text-primary);
  font-weight: 500;
}

.user-name-cell {
  color: var(--text-primary);
  font-weight: 500;
}

.amount-cell {
  color: var(--text-primary);
  font-weight: 600;
  font-size: 15px;
}

.gateway-cell {
  padding: 12px 16px;
}

.gateway-badge {
  display: inline-block;
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  text-transform: capitalize;
}

.gateway-badge.paddle {
  background: rgba(255, 152, 0, 0.15);
  color: #ff9800;
}

.gateway-badge.stripe {
  background: rgba(99, 102, 241, 0.15);
  color: #6366f1;
}

.subscription-cell {
  color: var(--text-secondary);
}

@keyframes slide-up {
  from { opacity: 0; transform: translateY(30px); }
  to { opacity: 1; transform: translateY(0); }
}

/* Responsive */
@media (max-width: 1024px) {
  .transactions-table-wrapper {
    overflow-x: auto;
  }

  .transactions-table {
    min-width: 800px;
  }
}

@media (max-width: 767px) {
  .payments-page {
    padding: 24px 16px;
  }

  .page-header h1 {
    font-size: 28px;
  }

  .subtitle {
    font-size: 14px;
  }

  .transactions-table th,
  .transactions-table td {
    padding: 12px 8px;
    font-size: 13px;
  }

  .transactions-table th {
    font-size: 12px;
  }
}
</style>
